// src/pages/Characters.js
// Character discovery grid → opens a 1:1 chat. Talks to /api/bots and /api/chat.

import { useCallback, useEffect, useState } from "react";
import Avatar from "../components/Avatar";
import ChatView from "../components/ChatView";
import { bots as botsApi } from "../api";

function Card({ bot, onOpen }) {
  return (
    <button className="card" onClick={() => onOpen(bot)}>
      <div className="card__media">
        <Avatar name={bot.name} url={bot.avatar_url} size={92} radius="20px" />
      </div>
      <div className="card__body">
        <div className="card__title">{bot.name}</div>
        <div className="card__desc">{bot.description || "An AI companion."}</div>
        <div className="card__meta">
          <span>💬 {bot.chat_count ?? 0}</span>
          <span>♥ {bot.like_count ?? 0}</span>
        </div>
      </div>
    </button>
  );
}

export default function Characters({ user, initialBot, onRequireAuth, onQuota }) {
  const [categories, setCategories] = useState([{ id: "all", name: "All" }]);
  const [active, setActive] = useState("all");
  const [sort, setSort] = useState("popular");
  const [search, setSearch] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [openBot, setOpenBot] = useState(null);

  useEffect(() => {
    botsApi.categories().then((d) => setCategories(d.categories || [])).catch(() => {});
  }, []);

  // Open a chat directly when arriving from "Create" (a just-made character).
  useEffect(() => {
    if (initialBot) setOpenBot(initialBot);
  }, [initialBot]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const d = await botsApi.list({ category: active, sort_by: sort, search, limit: 24 });
      setList(d.bots || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [active, sort, search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const open = (bot) => {
    if (!user) return onRequireAuth();
    setOpenBot(bot);
  };

  if (openBot) {
    return <ChatView bot={openBot} onBack={() => setOpenBot(null)} onQuota={onQuota} />;
  }

  return (
    <div className="page">
      <div className="page__head">
        <h1>Characters</h1>
        <p className="page__sub">Chat with immersive AI companions.</p>
      </div>

      <div className="searchbar">
        <input placeholder="Search characters…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <div className="toolbar">
        <div className="chips chips--scroll">
          {categories.map((c) => (
            <button key={c.id} className={`chip ${active === c.id ? "chip--active" : ""}`} onClick={() => setActive(c.id)}>
              {c.name}
            </button>
          ))}
        </div>
        <select className="sort" value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="popular">Most liked</option>
          <option value="chat_count">Most chatted</option>
          <option value="newest">Newest</option>
        </select>
      </div>

      {err && <div className="form-error center">{err}</div>}

      {loading ? (
        <div className="grid">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="card card--skeleton" />)}
        </div>
      ) : list.length === 0 ? (
        <div className="empty">No characters found.</div>
      ) : (
        <div className="grid">
          {list.map((b) => <Card key={b.id} bot={b} onOpen={open} />)}
        </div>
      )}
    </div>
  );
}
